import React, { useState, useEffect, useMemo, useCallback } from 'react'
import PropTypes from 'prop-types'
import { withRouter } from 'react-router-dom'
import _ from 'lodash'
import queryString from 'query-string'
import {
  Axios,
  BaseForm as Form,
  Button,
  Checkbox,
  DataTable,
  Grid,
  Table,
  ConditionWrapper,
  Segment,
} from 'component/base'
import { HanbaiTankaRirekiModal, ShiireTankaRirekiModal } from '../Modal'
import { Custom } from 'component/pieces'
import { useUserInfo } from 'component/hook'

export const Shohin = withRouter(props => {
  const {
    history,
    enableQueryCondition,
    multiple,
    selectedId,
    onSelectChange,
    refresh,
  } = props

  const { active_busho_id } = useUserInfo()

  const query = useMemo(
    () =>
      queryString.parse(window.location.search, {
        parseNumbers: true,
        parseBooleans: true,
      }),
    [window.location.search]
  )

  const path = useMemo(() => window.location.pathname, [
    window.location.pathname,
  ])

  const defaultCondition = useMemo(() => {
    return {
      busho_id: active_busho_id,
    }
  }, [active_busho_id])

  const [loading, setLoading] = useState(false)
  const [data, setData] = useState([])
  const [selectedIds, setSelectedIds] = useState([])
  const [condition, setCondition] = useState(
    _.assign({}, defaultCondition, enableQueryCondition ? query : {})
  )
  const [showShiireModal, setShowShiireModal] = useState(false)
  const [showHanbaiModal, setShowHanbaiModal] = useState(false)

  const [row_id, set_row_id] = useState(0)

  useEffect(() => {
    setSelectedIds(
      multiple
        ? selectedId && _.isArray(selectedId)
          ? selectedId
          : []
        : selectedId
        ? [selectedId]
        : []
    )
  }, [selectedId])

  useEffect(() => {
    onSelectChange(
      selectedIds,
      _.filter(data, row => _.find(selectedIds, id => row.id === id))
    )
  }, [selectedIds])

  useEffect(() => {
    setCondition(condition =>
      enableQueryCondition
        ? _(condition).assign(query).value()
        : _(condition).value()
    )
  }, [defaultCondition, enableQueryCondition, query])

  useEffect(() => {
    callApi(condition)
    setSelectedIds([])
  }, [condition, refresh])

  const handleConditionChange = (condition, isOnClear) => {
    setCondition(isOnClear ? defaultCondition : condition)
    if (enableQueryCondition)
      isOnClear
        ? history.push(`${path}`)
        : history.push(`${path}?${queryString.stringify(condition)}`)
  }

  const callApi = async condition => {
    setLoading(true)
    const { data, error } = await Axios.ajax.get('/api/staff/master/shohin', {
      params: { ...condition },
    })
    setLoading(false)
    if (error) return
    setData(data)
  }

  const handleChangeSelect = (selectedId, selected) => {
    setSelectedIds(selectedIds =>
      selected
        ? multiple
          ? _.union(selectedIds, [selectedId])
          : [selectedId]
        : _.filter(selectedIds, id => id !== selectedId)
    )
  }

  const TableRowWithProps = useCallback(
    props => (
      <TableRow
        {...props}
        history={history}
        handleChangeSelect={handleChangeSelect}
        setShowShiireModal={setShowShiireModal}
        setShowHanbaiModal={setShowHanbaiModal}
        set_row_id={set_row_id}
      />
    ),
    [history, handleChangeSelect, setShowShiireModal, setShowHanbaiModal]
  )

  return (
    <>
      <Segment basic loading={loading} style={{ padding: '0em' }}>
        <ConditionWrapper
          defaultCondition={defaultCondition}
          condition={condition}
          onConditionChange={handleConditionChange}
          FormElement={SearchElement}
          enableSidebar={false}
          compact={false}
        >
          <DataTable
            name='shohin'
            sortable
            filterable
            tableHeight='50vh'
            tableHeader={StaticWidthTableHeader}
            tableRow={TableRowWithProps}
            data={data}
            bottomControl={<></>}
            selectedIds={selectedIds}
          />
        </ConditionWrapper>

        {showShiireModal && (
          <ShiireTankaRirekiModal
            shohin_id={row_id}
            isOpenModal={showShiireModal}
            onClickCancel={() => setShowShiireModal(false)}
          />
        )}
        {showHanbaiModal && (
          <HanbaiTankaRirekiModal
            shohin_id={row_id}
            isOpenModal={showHanbaiModal}
            onClickCancel={() => setShowHanbaiModal(false)}
          />
        )}
      </Segment>
    </>
  )
})

Shohin.propTypes = {
  enableQueryCondition: PropTypes.bool,
  multiple: PropTypes.bool,
  selectedValue: PropTypes.oneOfType([PropTypes.string, PropTypes.array]),
  onSelectChange: PropTypes.func,
}

Shohin.defaultProps = {
  enableQueryCondition: false,
  multiple: false,
  selectedValue: '',
  onSelectChange: () => {},
}

const StaticWidthTableHeader = props => {
  const { withOrder, handleSort } = props

  return withOrder(
    <Table.Row>
      <Table.HeaderCell style={{ width: '3.0rem' }} content='#' />
      <Table.HeaderCell
        style={{ width: '8.0rem' }}
        onClick={() => handleSort('shohin_cd')}
        content='商品コード'
      />
      <Table.HeaderCell
        style={{ width: '20.0rem' }}
        onClick={() => handleSort('shohin_name')}
        content='商品名称'
      />
      <Table.HeaderCell
        style={{ width: '20.0rem' }}
        onClick={() => handleSort('shohin_kana')}
        content='商品カナ'
      />
      <Table.HeaderCell
        style={{ width: '8.0rem' }}
        onClick={() => handleSort('shohin_tani_kbn')}
        content='商品単位区分'
      />
      <Table.HeaderCell
        style={{ width: '8.0rem' }}
        onClick={() => handleSort('shiire_kazei_kbn')}
        content='仕入課税区分'
      />
      <Table.HeaderCell
        style={{ width: '8.0rem' }}
        onClick={() => handleSort('hanbai_kazei_kbn')}
        content='販売課税区分'
      />
      <Table.HeaderCell
        style={{ width: '8.0rem' }}
        onClick={() => handleSort('is_keigen_zei_rate')}
        content='軽減税率'
      />
      <Table.HeaderCell
        style={{ width: '8.0rem' }}
        onClick={() => handleSort('is_juchu_seisan')}
        content='受注生産'
      />
      <Table.HeaderCell style={{ width: '10.0rem' }} content='' />
      <Table.HeaderCell style={{ width: '10.0rem' }} content='' />
    </Table.Row>
  )
}

const TableRow = props => {
  const {
    withOrder,
    data,
    selectedIds,
    handleChangeSelect,
    setShowShiireModal,
    setShowHanbaiModal,
    set_row_id,
  } = props || {}

  const {
    id,
    shohin_cd,
    shohin_name,
    shohin_kana,
    shohin_tani_kbn,
    shiire_kazei_kbn,
    hanbai_kazei_kbn,
    is_keigen_zei_rate,
    is_juchu_seisan,
  } = data || {}

  const [isSelected, setIsSelected] = useState(
    selectedIds.indexOf(id) >= 0 ? true : false
  )

  const checkboxChange = (event, { value, checked }) => {
    event.stopPropagation()
    setIsSelected(checked)
    handleChangeSelect(value, checked)
  }

  return withOrder(
    <Table.Row
      positive={isSelected}
      onClick={() => {
        setIsSelected(state => !state)
        handleChangeSelect(id, !isSelected)
      }}
    >
      <Table.Cell
        style={{ width: '3.0rem' }}
        textAlign='center'
        content={
          <Checkbox checked={isSelected} onChange={checkboxChange} value={id} />
        }
      />
      <Table.Cell style={{ width: '8.0rem' }} content={shohin_cd} />
      <Table.Cell style={{ width: '20.0rem' }} content={shohin_name} />
      <Table.Cell style={{ width: '20.0rem' }} content={shohin_kana} />
      <Table.Cell
        style={{ width: '8.0rem' }}
        content={<Custom.Label.ShohinTaniKbn value={shohin_tani_kbn} />}
      />
      <Table.Cell
        style={{ width: '8.0rem' }}
        content={shiire_kazei_kbn === 'true' ? '課税' : '非課税'}
      />
      <Table.Cell
        style={{ width: '8.0rem' }}
        content={hanbai_kazei_kbn === 'true' ? '課税' : '非課税'}
      />
      <Table.Cell
        style={{ width: '8.0rem' }}
        content={is_keigen_zei_rate ? '〇' : '-'}
      />
      <Table.Cell
        style={{ width: '8.0rem' }}
        content={is_juchu_seisan ? '〇' : '-'}
      />
      <Table.Cell
        style={{ width: '10.0rem' }}
        content={
          <Button
            primary
            icon
            size='medium'
            onClick={e => {
              e.stopPropagation()
              setShowShiireModal(true)
              set_row_id(id)
            }}
            content='仕入単価履歴'
          />
        }
      />
      <Table.Cell
        style={{ width: '10.0rem' }}
        content={
          <Button
            primary
            icon
            size='medium'
            onClick={e => {
              e.stopPropagation()
              setShowHanbaiModal(true)
              set_row_id(id)
            }}
            content='販売単価履歴'
          />
        }
      />
    </Table.Row>
  )
}

const SearchElement = props => {
  const { nav, condition } = props

  return (
    <>
      <Grid.Column
        style={{
          display: !nav || condition.shohin_name ? 'block' : 'none',
        }}
      >
        <Form.Textbox.Standard
          value={condition.shohin_name}
          label='商品名称'
          name='shohin_name'
        />
      </Grid.Column>
      <Grid.Column
        style={{
          display: !nav || condition.shohin_cd ? 'block' : 'none',
        }}
      >
        <Form.Textbox.Standard
          value={condition.shohin_cd}
          label='商品コード'
          name='shohin_cd'
        />
      </Grid.Column>
    </>
  )
}
